// Projeto: Haklai Church — Plugin WordPress Modular - Desenvolvido por: Ricardo Sarmento - https://linx.pt

import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuthStore } from '../stores/authStore';
import { useTheme } from '../hooks/useTheme';
import { useMediaQuery } from '../hooks/useMediaQuery';
import Avatar from './Avatar';
import CheckpointLayout from './CheckpointLayout';

function AppLayout({ children, hideLayout = false }) {
  const isCheckpointContext = !!document.getElementById('haklai-checkpoint');

  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuthStore();
  const { theme, toggleTheme } = useTheme();

  // Detecta se está em mobile (max-width: 991px) - mesmo breakpoint do CSS
  const isMobile = useMediaQuery('(max-width: 991px)');

  // Estado do sidebar
  // Desktop: usa collapsed (colapsa para ícones)
  // Mobile: usa open (abre/fecha completamente)
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // Em mobile, fecha sidebar quando muda de rota
  useEffect(() => {
    if (isMobile) {
      setSidebarOpen(false);
    }
  }, [location.pathname, isMobile]);

  // Sincroniza estados quando muda de mobile para desktop
  useEffect(() => {
    if (!isMobile) {
      setSidebarOpen(false); // Remove classe 'open' em desktop
    }
  }, [isMobile]);

  // Se estivermos dentro do shortcode de checkpoint, delegamos para o CheckpointLayout
  if (isCheckpointContext) {
    return <CheckpointLayout hideLayout={hideLayout}>{children}</CheckpointLayout>;
  }

  const handleLogout = () => {
    logout();
    navigate('/login', { replace: true });
  };

  // Toggle sidebar com lógica condicional mobile/desktop
  // Desktop: colapsa/expande (collapsed)
  // Mobile: abre/fecha completamente (open)
  const toggleSidebar = () => {
    if (isMobile) {
      setSidebarOpen(!sidebarOpen);
    } else {
      setSidebarCollapsed(!sidebarCollapsed);
    }
  };

  // Fecha sidebar em mobile ao clicar no overlay
  const handleOverlayClick = () => {
    if (isMobile) {
      setSidebarOpen(false);
    }
  };

  // Determina qual item do menu está ativo baseado na rota atual
  const isActive = (path) => {
    if (path === '/dashboard') {
      return location.pathname === '/dashboard';
    }
    return location.pathname.startsWith(path);
  };

  // Helper para verificar permissões de menu
  // Consolida todas as permissões de todas as roles do utilizador
  const allRoles = user?.roles || [];
  const allPermissions = allRoles.flatMap(role => role.permissions || []);
  const allRoleSlugs = allRoles.map(role => role.slug);

  const hasAccess = (requiredLevel) => {
    // Acesso total se tiver a permissão específica ou se for o administrador do tenant
    if (allPermissions.includes('level:full_access')) return true;
    if (allRoleSlugs.includes('tenant_admin') || allRoleSlugs.includes('tannat') || allRoleSlugs.includes('admin')) return true;
    
    if (requiredLevel === 'gestao') {
        return true; // Todos que têm acesso ao dashboard podem ver gestão
    }
    
    if (requiredLevel === 'sistema') {
        if (allRoleSlugs.includes('pr_senior') || allRoleSlugs.includes('pr_supervisor') || allRoleSlugs.includes('pastor_senior') || allRoleSlugs.includes('pastor_supervisor')) {
            return true;
        }
        if (allPermissions.includes('level:dashboard_principal_gestao_sistema')) return true;
    }
    
    if (requiredLevel === 'principal') return true; // Todos que logaram tem acesso ao principal
    
    return false;
  };

  // Itens do menu organizados por seção
  const menuItems = [
    {
      section: 'Principal',
      show: true,
      items: [
        { path: '/dashboard', icon: 'dashboard', label: 'Dashboard' },
        { path: '/cells', icon: 'groups', label: 'Células' },
        { path: '/cells/map', icon: 'map', label: 'Mapa de Células' },
        { path: '/members', icon: 'person', label: 'Membros' },
        { path: '/agenda', icon: 'calendar_month', label: 'Agenda' },
        { path: '/help', icon: 'help', label: 'Ajuda' },
      ],
    },
    {
      section: 'Gestão',
      show: hasAccess('gestao'),
      items: [
        { path: '/checkpoint', icon: 'fact_check', label: 'Checkpoint' },
        { path: '/reports', icon: 'bar_chart', label: 'Relatórios' },
      ],
    },
    {
      section: 'Sistema',
      show: hasAccess('sistema'),
      items: [
        { path: '/import', icon: 'upload_file', label: 'Importar' },
        { path: '/roles', icon: 'admin_panel_settings', label: 'Funções' },
        { path: '/access', icon: 'vpn_key', label: 'Acessos' },
        { path: '/settings', icon: 'settings', label: 'Configurações' },
      ],
    },
  ].filter(section => section.show);

  // Classes condicionais para sidebar
  // Mobile: usa 'open' para mostrar/esconder
  // Desktop: usa 'collapsed' para colapsar/expandir
  const sidebarClasses = [
    'sidebar',
    isMobile ? (sidebarOpen ? 'open' : '') : (sidebarCollapsed ? 'collapsed' : ''),
  ].filter(Boolean).join(' ');

  // Se o layout estiver escondido (ex: Checkpoint), renderiza apenas o conteúdo
  if (hideLayout) {
    return <div className="haklai-app-minimal">{children}</div>;
  }

  return (
    <div className="dashboard-layout">
      {/* Overlay para mobile - aparece quando sidebar está aberto */}
      {isMobile && sidebarOpen && (
        <div
          className="sidebar-overlay"
          onClick={handleOverlayClick}
          aria-hidden="true"
        />
      )}

      {/* Sidebar */}
      <aside className={sidebarClasses}>
        <div className="sidebar-header">
          <div className="sidebar-logo">H</div>
          {(!sidebarCollapsed || isMobile) && <span className="sidebar-brand">Haklai</span>}
        </div>

        <nav className="sidebar-nav">
          {menuItems.map((section, sectionIndex) => (
            <div key={sectionIndex} className="nav-section">
              <div className="nav-section-title">{section.section}</div>
              {section.items.map((item) => (
                <div
                  key={item.path}
                  className={`nav-item ${isActive(item.path) ? 'active' : ''}`}
                  onClick={() => {
                    navigate(item.path);
                    // Fecha sidebar em mobile após navegar
                    if (isMobile) {
                      setSidebarOpen(false);
                    }
                  }}
                  style={{ cursor: 'pointer' }}
                >
                  <span className="material-symbols-outlined">{item.icon}</span>
                  {(!sidebarCollapsed || isMobile) && <span className="nav-item-text">{item.label}</span>}
                </div>
              ))}
            </div>
          ))}
        </nav>

        <div className="sidebar-footer">
          <div className="nav-item" onClick={handleLogout} style={{ cursor: 'pointer' }}>
            <span className="material-symbols-outlined">logout</span>
            {(!sidebarCollapsed || isMobile) && <span className="nav-item-text">Sair</span>}
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <main className={`main-content ${sidebarCollapsed && !isMobile ? 'collapsed' : ''}`}>
        {/* Header */}
        <header className="main-header">
          <div className="header-left">
            <div className="sidebar-toggle" onClick={toggleSidebar}>
              <span className="material-symbols-outlined">menu</span>
            </div>
            <div className="search-box">
              <span className="material-symbols-outlined" style={{ color: 'var(--text-muted)' }}>search</span>
              <input type="text" placeholder="Buscar membros, células, eventos..." />
            </div>
          </div>

          <div className="header-right">
            <button className="theme-toggle header-icon-btn" onClick={toggleTheme} title="Alternar tema">
              <span className={`material-symbols-outlined ${theme === 'light' ? 'icon-light' : 'icon-dark'}`}>
                {theme === 'light' ? 'light_mode' : 'dark_mode'}
              </span>
            </button>

            <div className="header-icon-btn">
              <span className="material-symbols-outlined">notifications</span>
              <div className="notification-badge"></div>
            </div>

            <div 
              className="user-menu" 
              onClick={() => navigate('/settings')}
              style={{ cursor: 'pointer' }}
              title="Configurações da Conta"
            >
              <div className="user-info">
                <div className="user-name">{user?.name || 'Usuário'}</div>
                <div className="user-role">{user?.roles?.[0]?.name || 'Membro'}</div>
              </div>
              <Avatar name={user?.name} size="md" />
            </div>
          </div>
        </header>

        {/* Page Content */}
        <div className="dashboard-content">
          {children}
        </div>
      </main>
    </div>
  );
}

export default AppLayout;
